import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { isDirectorOrAdmin } from '../utils/auth';
import '../App.css';

interface Club {
  id: string;
  name: string;
  logo?: string;
  colors: { primary: string; secondary: string };
  membershipFee?: number;
  teamFee?: number;
  multiTeamDiscountType?: string;
  multiTeamDiscountValue?: number;
}

interface Team {
  id: string;
  name: string;
  teamNumber: string;
  teamFee?: number;
  manager?: { id: string; firstName: string; lastName: string } | null;
  league?: { id: string; name: string } | null;
}

const ClubManagement: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [clubs, setClubs] = useState<Club[]>([]);
  const [clubTeams, setClubTeams] = useState<Record<string, Team[]>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user && !isDirectorOrAdmin(user.profileType)) {
      navigate('/clubs');
    }
  }, [user, navigate]);

  useEffect(() => {
    fetchMyClubs();
  }, []);

  const fetchMyClubs = async () => {
    try {
      const response = await api.get('/clubs/my-clubs');
      const clubsData = response.data.clubs || [];
      setClubs(clubsData);

      // Fetch teams for each club
      const teamsMap: Record<string, Team[]> = {};
      await Promise.all(
        clubsData.map(async (club: Club) => {
          try {
            const teamsResponse = await api.get(`/clubs/${club.id}/teams`);
            teamsMap[club.id] = teamsResponse.data.teams || [];
          } catch {
            teamsMap[club.id] = [];
          }
        })
      );
      setClubTeams(teamsMap);
    } catch (error) {
      console.error('Error fetching clubs:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingSpinner fullPage />;
  }

  return (
    <div className="container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1>My Clubs</h1>
        <button className="btn btn-primary" onClick={() => navigate('/clubs/create')}>
          Create New Club
        </button>
      </div>

      {clubs.length === 0 ? (
        <div className="card">
          <p>You don't have any clubs yet.</p>
          <button className="btn btn-primary" onClick={() => navigate('/clubs/create')} style={{ marginTop: '10px' }}>
            Create Your First Club
          </button>
        </div>
      ) : (
        clubs.map((club) => (
          <div key={club.id} className="card" style={{ marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '15px' }}>
              {club.logo ? (
                <img src={club.logo} alt={club.name} style={{ width: '60px', height: '60px', borderRadius: '12px' }} />
              ) : (
                <div
                  style={{
                    width: '60px',
                    height: '60px',
                    borderRadius: '12px',
                    backgroundColor: club.colors?.primary || '#333',
                    border: `3px solid ${club.colors?.secondary || '#fff'}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '20px',
                    fontWeight: 'bold'
                  }}
                >
                  {club.name.charAt(0)}
                </div>
              )}
              <div style={{ flex: 1 }}>
                <h2 style={{ margin: 0 }}>{club.name}</h2>
                <div style={{ display: 'flex', gap: '15px', marginTop: '5px', color: '#757575', fontSize: '14px' }}>
                  {club.membershipFee != null && club.membershipFee > 0 && (
                    <span>Membership: ${club.membershipFee}</span>
                  )}
                  {club.teamFee != null && club.teamFee > 0 && (
                    <span>Team Fee: ${club.teamFee}</span>
                  )}
                  {club.multiTeamDiscountType && club.multiTeamDiscountValue != null && (
                    <span>
                      Discount: {club.multiTeamDiscountType === 'percentage'
                        ? `${club.multiTeamDiscountValue}%`
                        : `$${club.multiTeamDiscountValue}`}
                    </span>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  className="btn btn-secondary"
                  onClick={() => navigate(`/clubs/${club.id}/edit`)}
                  style={{ fontSize: '14px', padding: '8px 15px' }}
                >
                  Edit
                </button>
                <Link
                  to={`/clubs/${club.id}`}
                  className="btn btn-primary"
                  style={{ fontSize: '14px', padding: '8px 15px', textDecoration: 'none' }}
                >
                  View
                </Link>
              </div>
            </div>

            <div style={{ borderTop: '1px solid #E0E0E0', paddingTop: '15px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <h3 style={{ margin: 0 }}>Teams ({(clubTeams[club.id] || []).length})</h3>
                <button
                  className="btn btn-primary"
                  onClick={() => navigate('/teams/create')}
                  style={{ fontSize: '13px', padding: '6px 12px' }}
                >
                  Add Team
                </button>
              </div>
              {(clubTeams[club.id] || []).length === 0 ? (
                <p style={{ color: '#757575' }}>No teams yet.</p>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '10px' }}>
                  {(clubTeams[club.id] || []).map((team) => (
                    <div
                      key={team.id}
                      style={{
                        padding: '12px',
                        border: '1px solid #E0E0E0',
                        borderRadius: '8px',
                        cursor: 'pointer'
                      }}
                      onClick={() => navigate(`/teams/${team.id}`)}
                    >
                      <strong>{team.name}</strong>
                      <div style={{ color: '#757575', fontSize: '13px' }}>{team.teamNumber}</div>
                      {team.teamFee != null && team.teamFee > 0 && (
                        <div style={{ fontSize: '13px' }}>Fee: ${team.teamFee}</div>
                      )}
                      {team.league && (
                        <div style={{ fontSize: '13px', color: '#2E7D32' }}>{team.league.name}</div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default ClubManagement;
